import React, { useState, useEffect, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import Swal from "sweetalert2";
import { host } from "../../../configuraciones";
import { obtenerCoccionRecords, guardarCoccionRecord, actualizarEstatusCoccionRecord } from "../../../redux/DucksBeneficiario/BitacoraCoccionDucks";
import { obtenerMoliendaRecords, guardarMoliendaRecord, actualizarEstatusMoliendaRecord } from "../../../redux/DucksBeneficiario/BitacoraMoliendaDucks";
import { obtenerMaduracionRecords, guardarMaduracionRecord, actualizarMaduracionRecord, actualizarEstatusMaduracionRecord } from "../../../redux/DucksBeneficiario/BitacoraMaduracionDucks";
import { obtenerCondicionesRecords, guardarCondicionesRecord, actualizarCondicionesRecord, actualizarEstatusCondicionesRecord } from "../../../redux/DucksBeneficiario/BitacoraCondicionesDucks";
import { obtenerClienteByIdLogin } from "../../../redux/DucksBeneficiario/ClienteDucks";
import "./BitacoraProduccion.css";

// Material UI Icons
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import ScienceIcon from '@mui/icons-material/Science';
import WineBarIcon from '@mui/icons-material/WineBar';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import GrassIcon from '@mui/icons-material/Grass';
import LayersIcon from '@mui/icons-material/Layers';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import CancelIcon from '@mui/icons-material/Cancel';

// Subcomponents
import CoccionSection from "./BitacoraProduccion/CoccionSection";
import MoliendaSection from "./BitacoraProduccion/MoliendaSection";
import DestilacionSection from "./BitacoraProduccion/DestilacionSection";
import GranelSection from "./BitacoraProduccion/GranelSection";
import ElaboracionSection from "./BitacoraProduccion/ElaboracionSection";
import MaduracionSection from "./BitacoraProduccion/MaduracionSection";
import CondicionesSection from "./BitacoraProduccion/CondicionesSection";
import QuejasSection from "./BitacoraProduccion/QuejasSection";

const BitacoraProduccion = () => {
  const dispatch = useDispatch();
  const currentUser = useSelector(state => state.Usuario?.data);
  const idLogin = currentUser?.user?.[0]?.id || currentUser?.idlogin || currentUser?.id_login || currentUser?.id || null;
  const { records: coccionApiRecords, pagination: coccionPagination } = useSelector(state => state.bitacoraCoccion || { records: [], pagination: {} });
  const { records: moliendaApiRecords } = useSelector(state => state.bitacoraMolienda || { records: [] });
  const { records: maduracionApiRecords } = useSelector(state => state.bitacoraMaduracion || { records: [] });
  const { records: condicionesApiRecords } = useSelector(state => state.bitacoraCondiciones || { records: [] });

  const [coccionPage, setCoccionPage] = useState(1);
  const [coccionSearch, setCoccionSearch] = useState("");

  const [instalaciones, setInstalaciones] = useState([]);
  const fetchCoccionData = useCallback((page = 1, search = "") => {
    dispatch(obtenerCoccionRecords({ idLogin, page, limit: 10, search }));
  }, [dispatch, idLogin]);

  const fetchMoliendaData = useCallback(() => {
    dispatch(obtenerMoliendaRecords({ idLogin, page: 1, limit: 50 }));
  }, [dispatch, idLogin]);

  const fetchMaduracionData = useCallback(() => {
    dispatch(obtenerMaduracionRecords({ idLogin, page: 1, limit: 50 }));
  }, [dispatch, idLogin]);

  const fetchCondicionesData = useCallback(() => {
    dispatch(obtenerCondicionesRecords({ idLogin, page: 1, limit: 50 }));
  }, [dispatch, idLogin]);

  useEffect(() => {
    fetchCoccionData(coccionPage, coccionSearch);
    fetchMoliendaData();
    fetchMaduracionData();
    fetchCondicionesData();
  }, [fetchCoccionData, fetchMoliendaData, fetchMaduracionData, fetchCondicionesData, coccionPage, coccionSearch]);

  useEffect(() => {
    const fetchInstalaciones = async () => {
      if (!idLogin) return;
      try {
        const resCliente = await axios.get(`${host}InformacionValida/obtenerClienteByIdLogin/${idLogin}`);
        const idCliente = resCliente.data?.data?.cliente?.[0]?.idcliente;
        if (idCliente) {
          const resAnexos = await axios.get(`${host}InformacionValida/obtenerAnexosCliente/${idCliente}`);
          if (resAnexos.data?.data) {
            // Filtrar únicamente los anexos del cliente que corresponden a Unidad de Producción (Tipo de Anexo 1)
            const anexosProduccion = resAnexos.data.data.filter(
              anexo => Number(anexo.idtipoanexo || anexo.idtipo_anexo || anexo.tipoanexo?.idtipoanexo) === 1
            );
            setInstalaciones(anexosProduccion);
          }
        }
      } catch (error) {
        console.error("Error al obtener instalaciones del cliente:", error);
      }
    };
    fetchInstalaciones();
  }, [idLogin]);

  const handleCoccionSearchChange = (val) => {
    setCoccionSearch(val);
    setCoccionPage(1);
  };

  const handleCoccionPageChange = (newPage) => {
    setCoccionPage(newPage);
  };

  // Tab navigation state
  const [activeTab, setActiveTab] = useState(0);
  const [isFormOpen, setIsFormOpen] = useState(false);

  const tabsInfo = [
    { title: "Sección II", name: "Cocción", desc: "Ingreso de agave y horneado", icon: LocalFireDepartmentIcon, theme: "status-coccion" },
    { title: "Sección III (A)", name: "Molienda & Fermentación", desc: "Formulación y monitoreo diario", icon: ScienceIcon, theme: "status-molienda" },
    { title: "Sección III (B) & IV", name: "Destilación", desc: "Ordinario y refinado de cortes", icon: WineBarIcon, theme: "status-destilacion" },
    { title: "Sección V", name: "Granel", desc: "Inventario final a granel", icon: CheckCircleIcon, theme: "status-cierre" },
    { title: "Sección VI", name: "Clases & Reprocesos", desc: "Abocado, destilado con, reprocesos", icon: LayersIcon, theme: "status-elaboracion" },
    { title: "Sección VII", name: "Maduración", desc: "Inventario a granel en maduración", icon: WineBarIcon, theme: "status-destilacion" },
    { title: "Sección VIII", name: "Condiciones", desc: "Monitoreo de temp. y humedad", icon: ScienceIcon, theme: "status-molienda" },
    { title: "Sección IX", name: "Quejas", desc: "Quejas del Producto Certificado", icon: ReportProblemIcon, theme: "status-coccion" }
  ];

  // ================= DATA HISTORY STATES (INDEPENDENT RECORDS) =================
  const [coccionRecords, setCoccionRecords] = useState([]);

  // Unificar registros de API hostUI con la lista
  const displayCoccionRecords = useMemo(() => {
    if (coccionApiRecords && coccionApiRecords.length > 0) {
      return coccionApiRecords.map(r => ({
        id: r.id ? `COC-${String(r.id).padStart(2, '0')}` : r.noProduccion,
        rawId: r.id,
        idUnidadProduccion: r.id_unidad_produccion || r.idUnidadProduccion || null,
        noProduccion: r.noProduccion || "-",
        pinas: r.numPinas !== undefined && r.numPinas !== null ? String(r.numPinas) : "0",
        kgMaguey: r.kgMaguey ? `${Number(r.kgMaguey).toLocaleString()} kg` : "0 kg",
        fechaIngreso: r.fechaIngreso || "-",
        guiaPredio: r.guiaPredio || "-",
        tipoMaguey: r.tipoMaguey || "-",
        pctArt: r.pctArt ? `${r.pctArt}%` : "-",
        noInforme: r.noInforme || "-",
        fechaInicio: r.fechaInicioCoccion || "-",
        fechaFin: r.fechaFinCoccion || "-",
        kgMagueyCocido: r.kgMagueyCocido ? `${Number(r.kgMagueyCocido).toLocaleString()} kg` : "0 kg",
        observaciones: r.observaciones || "Sin observaciones.",
        observaciones_correccion: r.observaciones_correccion || r.observacionesCorreccion || "",
        observacionesCorreccion: r.observacionesCorreccion || r.observaciones_correccion || "",
        inspector: r.inspector || "Pendiente de V.Bo.",
        status: r.status || "borrador"
      }));
    }
    return coccionRecords;
  }, [coccionApiRecords, coccionRecords]);

  const [moliendaRecords, setMoliendaRecords] = useState([
    {
      id: "MOL-01",
      noProduccion: "PROD-LOTE-01",
      observaciones: "Fermentación espontánea natural usando levaduras autóctonas.",
      inspector: "Ing. Carlos Mendoza (24/07/2026)",
      status: "aprobado",
      items: [
        {
          id: 1,
          fechaFermentacion: "2026-07-20",
          contenedor: "Tina 03",
          kgMaguey: "8,500 kg",
          fechaFormulacion: "2026-07-21",
          volumenFormulado: "3,200 L",
          fechaPrimeraDestilacion: "2026-07-24",
          volumenMezcal: "1,250 L",
          pctAlcMezcal: "26.5%",
          volumenColas: "180 L",
          pctAlcColas: "15.0%"
        }
      ]
    }
  ]);

  const displayMoliendaRecords = useMemo(() => {
    if (moliendaApiRecords && moliendaApiRecords.length > 0) {
      return moliendaApiRecords.map(r => ({
        id: r.id ? `MOL-${String(r.id).padStart(2, '0')}` : r.noProduccion,
        rawId: r.id,
        noProduccion: r.noProduccion || "-",
        observaciones: r.observaciones || "Sin observaciones.",
        inspector: r.inspector || "Pendiente de V.Bo.",
        status: r.status || "borrador",
        items: (r.items || []).map(item => ({
          ...item,
          kgMaguey: item.kgMaguey ? `${Number(item.kgMaguey).toLocaleString()} kg` : "0 kg",
          volumenFormulado: item.volumenFormulado ? `${Number(item.volumenFormulado).toLocaleString()} L` : "0 L",
          volumenMezcal: item.volumenMezcal ? `${Number(item.volumenMezcal).toLocaleString()} L` : "0 L",
          pctAlcMezcal: item.pctAlcMezcal ? `${item.pctAlcMezcal}%` : "0%",
          volumenColas: item.volumenColas ? `${Number(item.volumenColas).toLocaleString()} L` : "0 L",
          pctAlcColas: item.pctAlcColas ? `${item.pctAlcColas}%` : "0%"
        }))
      }));
    }
    return moliendaRecords;
  }, [moliendaApiRecords, moliendaRecords]);


  const [destilacionRecords, setDestilacionRecords] = useState([
    {
      id: "DES-01",
      noProduccion: "PROD-LOTE-01",
      fechaSegunda: "2026-07-25",
      destiladorNum: "Destilador Cobre 02",
      volADestilar: "1,200 L",
      puntas: "45 L (65.0%)",
      mezcal: "480 L (48.5%)",
      colas: "85 L (18.0%)",
      observaciones: "Destilación lenta a fuego medio.",
      inspector: "Ing. Carlos Mendoza (25/07/2026)",
      status: "aprobado",
      items: [
        {
          id: 1,
          fechaSegunda: "2026-07-25",
          destiladorNum: "Destilador Cobre 02",
          volADestilar: "1200",
          esDestiladoCon: false,
          destiladoConIngrediente: "",
          puntasVol: "45",
          puntasAlc: "65.0",
          mezcalVol: "480",
          mezcalAlc: "48.5",
          colasVol: "85",
          colasAlc: "18.0"
        }
      ]
    }
  ]);

  const [granelRecords, setGranelRecords] = useState([
    {
      id: "GRA-01",
      fechaMovimiento: "2026-07-26",
      idContenedor: "C-04",
      noLote: "PROD-LOTE-01",
      categoria: "Mezcal Artesanal",
      clase: "Joven / Blanco",
      ingredientes: "Maguey Espadín",
      edad: "Sin edad",
      tipoMaguey: "Espadín",
      noAnalisisFisicoquimico: "ANA-2026-098",
      invInicialVol: "0 L",
      invInicialAlc: "0.0%",
      entradaProcedencia: "Destilación Lote 01",
      entradaVolumen: "480 L",
      entradaAlc: "48.5%",
      entradaVolAgua: "15 L",
      salidaVolumen: "0 L",
      salidaAlc: "0.0%",
      salidaDestino: "N/A",
      invFinalVol: "495 L",
      invFinalAlc: "47.0%",
      observaciones: "Ajuste alcohólico con agua destilada.",
      inspector: "Ing. Carlos Mendoza (26/07/2026)",
      status: "aprobado"
    }
  ]);

  const [elaboracionRecords, setElaboracionRecords] = useState([
    {
      id: "ELAB-01",
      fechaIngreso: "2026-07-27",
      loteOrigen: "PROD-LOTE-01",
      volumenOrigen: "495",
      pctAlcOrigen: "47.0",
      categoriaOrigen: "Mezcal Artesanal",
      claseOrigen: "Joven / Blanco",
      ingredientesOrigen: "Maguey Espadín",
      edadOrigen: "Sin edad",
      analisisOrigen: "ANA-2026-098",
      tipoMagueyOrigen: "Espadín",
      ingredientesAdicionados: "Hierbabuena y cáscara de naranja",
      procesoRealizado: "ABOCADO CON",
      descripcionProceso: "Abocado por infusión directa durante 24 horas",
      fechaSalida: "2026-07-28",
      loteSalida: "PROD-LOTE-01-ABO",
      volumenSalida: "490",
      pctAlcSalida: "46.5",
      claseSalida: "Abocado con",
      observaciones: "Estabilizado térmicamente antes de salida.",
      inspector: "Ing. Carlos Mendoza (28/07/2026)",
      status: "aprobado"
    }
  ]);

  const [maduracionRecords, setMaduracionRecords] = useState([
    {
      id: "MAD-01",
      fechaMovimiento: "2026-07-28",
      noLote: "PROD-LOTE-01",
      categoria: "Mezcal Artesanal",
      clase: "Reposado",
      ingredientes: "Maguey Espadín",
      edad: "0 meses",
      tipoMaguey: "Espadín",
      noAnalisis: "ANA-2026-098",
      tipoRecipiente: "BARRICAS DE MADERA DE",
      detalleRecipiente: "Roble Blanco Americano - Tostado Medio",
      invInicialFecha: "2026-07-28",
      invInicialRecipientes: "2",
      invInicialVolumen: "400",
      invInicialAlc: "48.5",
      entradaFolio: "FOL-MAD-001",
      entradaRecipientes: "2",
      entradaVolumen: "400",
      entradaAlc: "48.5",
      salidaRecipientes: "0",
      salidaVolumen: "0",
      salidaAlc: "0.0",
      salidaClaseObtenida: "N/A",
      salidaEdadObtenida: "0 meses",
      salidaMerma: "0",
      invFinalRecipientes: "2",
      invFinalVolumen: "400",
      invFinalAlc: "48.5",
      observaciones: "Inicio de proceso de reposo en barrica nueva.",
      inspector: "Ing. Carlos Mendoza (28/07/2026)",
      status: "aprobado"
    }
  ]);

  const [condicionesRecords, setCondicionesRecords] = useState([
    {
      id: "COND-01",
      mesAnio: "Julio 2026",
      noLote: "PROD-LOTE-01",
      registros: [
        { dia: 1, hora: "09:00", temp: "22.5", hum: "62.0" },
        { dia: 2, hora: "09:15", temp: "23.0", hum: "60.5" },
        { dia: 3, hora: "08:45", temp: "21.8", hum: "63.0" },
        { dia: 4, hora: "09:30", temp: "22.1", hum: "61.5" },
        { dia: 5, hora: "09:00", temp: "22.7", hum: "62.4" },
        ...Array.from({ length: 26 }, (_, i) => ({ dia: i + 6, hora: "", temp: "", hum: "" }))
      ],
      observaciones: "Condiciones estables en bodega de añejamiento principal.",
      inspector: "Ing. Carlos Mendoza (31/07/2026)",
      status: "aprobado"
    }
  ]);

  const [quejasRecords, setQuejasRecords] = useState([
    {
      id: "QJ-PROD-01",
      fechaQueja: "2026-07-10",
      noQueja: "QJ-2026-001",
      noLote: "PROD-LOTE-01",
      descripcionProducto: "Mezcal Artesanal Joven / Blanco - Maguey Espadín",
      detalleQueja: "El cliente reportó precipitado blanquecino en fondo de botellas.",
      fechaAtencion: "2026-07-12",
      medidasCorrectivas: "Se realizó una filtración fina adicional al resto del lote a granel para asegurar limpidez.",
      inspector: "Ing. Carlos Mendoza (12/07/2026)",
      status: "aprobado"
    }
  ]);

  // Form states for Section IX (Quejas)
  const [quejaFechaQueja, setQuejaFechaQueja] = useState("2026-07-19");
  const [quejaNoQueja, setQuejaNoQueja] = useState("");
  const [quejaNoLote, setQuejaNoLote] = useState("manual");
  const [quejaDescripcionProducto, setQuejaDescripcionProducto] = useState("");
  const [quejaDetalleQueja, setQuejaDetalleQueja] = useState("");
  const [quejaFechaAtencion, setQuejaFechaAtencion] = useState("");
  const [quejaMedidasCorrectivas, setQuejaMedidasCorrectivas] = useState("");

  // ================= FORM STATES (COCCIÓN OBJECT FORM DATA) =================
  const [formDataCoccion, setFormDataCoccion] = useState({
    idUnidadProduccion: "",
    noProduccion: "",
    numPinas: "",
    kgMaguey: "",
    fechaIngreso: "",
    guiaPredio: "",
    tipoMaguey: "",
    pctArt: "",
    noInforme: "",
    fechaInicioCoccion: "",
    fechaFinCoccion: "",
    kgMagueyCocido: "",
    observaciones: ""
  });

  const magueySpecies = [
    { name: "Espadín", scientific: "Agave angustifolia" },
    { name: "Tobalá", scientific: "Agave potatorum" },
    { name: "Tepeztate", scientific: "Agave marmorata" },
    { name: "Arroqueño", scientific: "Agave americana" },
    { name: "Cuishe", scientific: "Agave karwinskii" },
    { name: "Papalometl", scientific: "Agave cupreata" },
    { name: "Cenizo", scientific: "Agave durangensis" }
  ];

  // Tab 2: Molienda
  const [formDataMolienda, setFormDataMolienda] = useState({
    noProduccion: "",
    observaciones: "",
    items: []
  });

  // Tab 3: Destilación
  const [formDataDestilacion, setFormDataDestilacion] = useState({
    noProduccion: "",
    observaciones: "",
    items: []
  });

  // Tab 4: Granel
  const [formDataGranel, setFormDataGranel] = useState({
    fechaMovimiento: "",
    idContenedor: "",
    noLote: "",
    categoria: "",
    clase: "",
    ingredientes: "",
    edad: "",
    tipoMaguey: "",
    noAnalisis: "",
    invInicialVol: "",
    invInicialAlc: "",
    entradaProcedencia: "",
    entradaVolumen: "",
    entradaAlc: "",
    entradaVolAgua: "",
    salidaVolumen: "",
    salidaAlc: "",
    salidaDestino: "",
    invFinalVol: "",
    invFinalAlc: "",
    observaciones: ""
  });

  // Tab 5: Elaboracion (Sección VI)
  const [formDataElaboracion, setFormDataElaboracion] = useState({
    fechaIngreso: "",
    loteOrigen: "",
    volumenOrigen: "",
    pctAlcOrigen: "",
    categoriaOrigen: "",
    claseOrigen: "",
    ingredientesOrigen: "",
    edadOrigen: "",
    analisisOrigen: "",
    tipoMagueyOrigen: "",
    ingredientesAdicionados: "",
    procesoRealizado: "",
    descripcionProceso: "",
    fechaSalida: "",
    loteSalida: "",
    volumenSalida: "",
    pctAlcSalida: "",
    claseSalida: "",
    observaciones: ""
  });

  // Tab 6: Maduracion (Sección VII)
  const [formDataMaduracion, setFormDataMaduracion] = useState({
    fechaMovimiento: "",
    noLote: "",
    categoria: "",
    clase: "",
    ingredientes: "",
    edad: "",
    tipoMaguey: "",
    noAnalisis: "",
    tipoRecipiente: "",
    detalleRecipiente: "",
    invInicialFecha: "",
    invInicialRecipientes: "",
    invInicialVolumen: "",
    invInicialAlc: "",
    entradaFolio: "",
    entradaRecipientes: "",
    entradaVolumen: "",
    entradaAlc: "",
    salidaRecipientes: "",
    salidaVolumen: "",
    salidaAlc: "",
    salidaClaseObtenida: "",
    salidaEdadObtenida: "",
    salidaMerma: "",
    invFinalRecipientes: "",
    invFinalVolumen: "",
    invFinalAlc: "",
    observaciones: ""
  });

  // Tab 7: Condiciones (Sección VIII)
  const [formDataCondiciones, setFormDataCondiciones] = useState({
    mesAnio: "",
    noLote: "",
    dailyData: Array.from({ length: 31 }, (_, i) => ({ dia: i + 1, hora: "", temp: "", hum: "" })),
    observaciones: ""
  });

  // Tab 8: Quejas (Sección IX)
  const [formDataQuejas, setFormDataQuejas] = useState({
    fechaQueja: "",
    noQueja: "",
    noLote: "",
    descripcionProducto: "",
    detalleQueja: "",
    fechaAtencion: "",
    medidasCorrectivas: ""
  });

  // Submit handlers
  const handleSaveCoccion = async (status) => {
    const pctArtNum = formDataCoccion.pctArt !== "" && formDataCoccion.pctArt !== null && formDataCoccion.pctArt !== undefined
      ? Number(formDataCoccion.pctArt)
      : null;

    if (pctArtNum !== null && (isNaN(pctArtNum) || pctArtNum < 0 || pctArtNum > 100)) {
      Swal.fire({
        title: "Atención",
        text: "El Porcentaje de Azúcares Reductores Totales (% ART) debe ser un valor entre 0 y 100.",
        icon: "warning",
        confirmButtonColor: "#4A5D23"
      });
      return;
    }

    if (formDataCoccion.fechaIngreso && formDataCoccion.fechaInicioCoccion) {
      if (new Date(formDataCoccion.fechaInicioCoccion) < new Date(formDataCoccion.fechaIngreso)) {
        Swal.fire({
          title: "Inconsistencia en Fechas",
          text: "La Fecha Inicial de Cocción no puede ser anterior a la Fecha de Ingreso del Maguey.",
          icon: "warning",
          confirmButtonColor: "#4A5D23"
        });
        return;
      }
    }

    if (formDataCoccion.fechaInicioCoccion && formDataCoccion.fechaFinCoccion) {
      if (new Date(formDataCoccion.fechaFinCoccion) < new Date(formDataCoccion.fechaInicioCoccion)) {
        Swal.fire({
          title: "Inconsistencia en Fechas",
          text: "La Fecha Final de Cocción no puede ser anterior a la Fecha Inicial de Cocción.",
          icon: "warning",
          confirmButtonColor: "#4A5D23"
        });
        return;
      }
    }

    const recordPayload = {
      idLogin,
      idUnidadProduccion: formDataCoccion.idUnidadProduccion ? Number(formDataCoccion.idUnidadProduccion) : null,
      noProduccion: formDataCoccion.noProduccion,
      numPinas: Number(formDataCoccion.numPinas || 0),
      kgMaguey: Number(formDataCoccion.kgMaguey || 0),
      fechaIngreso: formDataCoccion.fechaIngreso,
      guiaPredio: formDataCoccion.guiaPredio,
      tipoMaguey: formDataCoccion.tipoMaguey,
      pctArt: pctArtNum,
      noInforme: formDataCoccion.noInforme,
      fechaInicioCoccion: formDataCoccion.fechaInicioCoccion,
      fechaFinCoccion: formDataCoccion.fechaFinCoccion,
      kgMagueyCocido: Number(formDataCoccion.kgMagueyCocido || 0),
      observaciones: formDataCoccion.observaciones,
      status
    };

    try {
      const rawId = formDataCoccion.rawId || (formDataCoccion.id && typeof formDataCoccion.id === 'number' ? formDataCoccion.id : null);
      if (rawId) {
        await dispatch(actualizarCoccionRecord({ id: rawId, recordData: recordPayload })).unwrap();
      } else {
        await dispatch(guardarCoccionRecord(recordPayload)).unwrap();
      }
      setIsFormOpen(false);
      fetchCoccionData(coccionPage, coccionSearch);
      Swal.fire({
        title: status === "revision" ? "¡Solicitud Enviada!" : "¡Borrador Guardado!",
        text: status === "revision" ? "¡Bitácora de Cocción enviada para revisión oficial!" : "Borrador de Cocción guardado exitosamente.",
        icon: "success",
        confirmButtonColor: "#4A5D23"
      });
    } catch (err) {
      Swal.fire({
        title: "Error al Guardar",
        text: err?.mensaje || err?.message || (typeof err === "string" ? err : "Error al guardar el registro de cocción"),
        icon: "error",
        confirmButtonColor: "#4A5D23"
      });
    }
  };

  const handleSaveMolienda = async (status) => {
    if (!formDataMolienda.noProduccion) {
      alert("Por favor selecciona un Número de Producción.");
      return;
    }
    if (!formDataMolienda.items || formDataMolienda.items.length === 0) {
      alert("Debe agregar al menos una tina/contenedor de fermentación.");
      return;
    }

    const payload = {
      idLogin,
      noProduccion: formDataMolienda.noProduccion,
      observaciones: formDataMolienda.observaciones,
      status,
      items: formDataMolienda.items.map(item => ({
        fechaFermentacion: item.fechaFermentacion,
        contenedor: item.contenedor,
        kgMaguey: Number(item.kgMaguey || 0),
        fechaFormulacion: item.fechaFormulacion,
        volumenFormulado: Number(item.volumenFormulado || 0),
        fechaPrimeraDestilacion: item.fechaPrimeraDestilacion,
        volumenMezcal: Number(item.volumenMezcal || 0),
        pctAlcMezcal: Number(item.pctAlcMezcal || 0),
        volumenColas: Number(item.volumenColas || 0),
        pctAlcColas: Number(item.pctAlcColas || 0)
      }))
    };

    try {
      await dispatch(guardarMoliendaRecord(payload)).unwrap();
      setIsFormOpen(false);
      fetchMoliendaData();
      alert(status === "revision" ? "¡Bitácora de Molienda enviada para revisión oficial!" : "Borrador de Molienda guardado exitosamente.");
    } catch (err) {
      alert(err?.mensaje || err?.message || (typeof err === "string" ? err : "Error al guardar el registro de molienda"));
    }
  };

  const [statusModal, setStatusModal] = useState({
    isOpen: false,
    record: null,
    actionType: "", // "rechazado" | "cancelada"
    section: "",    // "coccion" | "molienda" | "destilacion" | "granel"
    observaciones: ""
  });

  const handleOpenRejectModal = (record, section) => {
    setStatusModal({
      isOpen: true,
      record,
      actionType: "rechazado",
      section,
      observaciones: record.observaciones_correccion || record.observacionesCorreccion || ""
    });
  };

  const handleOpenCancelModal = (record, section) => {
    if (record?.status === "aprobado" || record?.status === "aceptada" || record?.status === "aceptado") {
      Swal.fire({
        title: "Acción restringida",
        text: "No se puede cancelar una bitácora que ya ha sido aprobada o aceptada por la Unidad de Inspección.",
        icon: "warning",
        confirmButtonColor: "#4A5D23"
      });
      return;
    }
    setStatusModal({
      isOpen: true,
      record,
      actionType: "cancelada",
      section,
      observaciones: record.observaciones_correccion || record.observacionesCorreccion || ""
    });
  };

  const handleConfirmStatusAction = async () => {
    const { record, actionType, section, observaciones } = statusModal;
    if (!observaciones.trim()) {
      Swal.fire({
        title: "Campo Requerido",
        text: `Por favor ingrese las observaciones / motivos para ${actionType === "rechazado" ? "rechazar para corrección" : "cancelar"} esta bitácora.`,
        icon: "warning",
        confirmButtonColor: "#4A5D23"
      });
      return;
    }

    const rawId = record?.rawId || record?.id;

    try {
      if (section === "coccion") {
        if (rawId) {
          await dispatch(actualizarEstatusCoccionRecord({ id: rawId, estatus: actionType, observacionesCorreccion: observaciones })).unwrap();
          fetchCoccionData(coccionPage, coccionSearch);
        }
      } else if (section === "molienda") {
        if (rawId) {
          await dispatch(actualizarEstatusMoliendaRecord({ id: rawId, estatus: actionType, observacionesCorreccion: observaciones })).unwrap();
          fetchMoliendaData();
        }
      } else if (section === "destilacion") {
        setDestilacionRecords(prev => prev.map(rec => (rec.id === record.id || rec.noProduccion === record.noProduccion) ? { ...rec, status: actionType, observaciones_correccion: observaciones } : rec));
      } else if (section === "granel") {
        setGranelRecords(prev => prev.map(rec => (rec.id === record.id || rec.idContenedor === record.idContenedor) ? { ...rec, status: actionType, observaciones_correccion: observaciones } : rec));
      } else if (section === "maduracion") {
        if (rawId) {
          await dispatch(actualizarEstatusMaduracionRecord({ id: rawId, estatus: actionType, observacionesCorreccion: observaciones })).unwrap();
          fetchMaduracionData();
        } else {
          setMaduracionRecords(prev => prev.map(rec => (rec.id === record.id || rec.noLote === record.noLote) ? { ...rec, status: actionType, observaciones_correccion: observaciones } : rec));
        }
      } else if (section === "condiciones") {
        if (rawId) {
          await dispatch(actualizarEstatusCondicionesRecord({ id: rawId, estatus: actionType, observacionesCorreccion: observaciones })).unwrap();
          fetchCondicionesData();
        } else {
          setCondicionesRecords(prev => prev.map(rec => (rec.id === record.id) ? { ...rec, status: actionType, observaciones_correccion: observaciones } : rec));
        }
      }

      setStatusModal({ isOpen: false, record: null, actionType: "", section: "", observaciones: "" });
      Swal.fire({
        title: "Estatus Actualizado",
        text: `La bitácora de ${section.toUpperCase()} ha sido ${actionType === 'rechazado' ? 'rechazada para corrección' : 'cancelada'}.`,
        icon: "success",
        confirmButtonColor: "#4A5D23"
      });
    } catch (err) {
      Swal.fire({
        title: "Error al Actualizar",
        text: err?.mensaje || err?.message || "Error al actualizar el estatus de la bitácora.",
        icon: "error",
        confirmButtonColor: "#4A5D23"
      });
    }
  };

  const handleCancelCoccion = (record) => handleOpenCancelModal(record, "coccion");
  const handleRejectCoccion = (record) => handleOpenRejectModal(record, "coccion");

  const handleCancelMolienda = (record) => handleOpenCancelModal(record, "molienda");
  const handleRejectMolienda = (record) => handleOpenRejectModal(record, "molienda");

  const handleCancelDestilacion = (record) => handleOpenCancelModal(record, "destilacion");
  const handleRejectDestilacion = (record) => handleOpenRejectModal(record, "destilacion");

  const handleCancelGranel = (record) => handleOpenCancelModal(record, "granel");
  const handleRejectGranel = (record) => handleOpenRejectModal(record, "granel");

  const handleSaveDestilacion = (status) => {
    const itemsList = formDataDestilacion.items || [];
    const formattedItems = itemsList.map(item => ({
      ...item,
      volADestilar: item.volADestilar ? `${parseFloat(item.volADestilar).toLocaleString()} L` : "0 L",
      puntas: item.puntasVol ? `${parseFloat(item.puntasVol).toLocaleString()} L (${item.puntasAlc || 0}%)` : "-",
      mezcal: item.mezcalVol ? `${parseFloat(item.mezcalVol).toLocaleString()} L (${item.mezcalAlc || 0}%)` : "-",
      colas: item.colasVol ? `${parseFloat(item.colasVol).toLocaleString()} L (${item.colasAlc || 0}%)` : "-",
      tipoObtenido: item.esDestiladoCon ? `Destilado con ${item.destiladoConIngrediente || ''}`.trim() : "Blanco / Joven"
    }));

    const firstItem = formattedItems[0] || {};

    const newRecord = {
      id: `DES-0${destilacionRecords.length + 1}`,
      noProduccion: formDataDestilacion.noProduccion,
      fechaSegunda: firstItem.fechaSegunda || "",
      destiladorNum: firstItem.destiladorNum || "Destilador Cobre 02",
      volADestilar: firstItem.volADestilar || "0 L",
      puntas: firstItem.puntas || "-",
      mezcal: firstItem.mezcal || "-",
      colas: firstItem.colas || "-",
      observaciones: formDataDestilacion.observaciones || "Sin observaciones.",
      inspector: "Pendiente de V.Bo.",
      status: status,
      items: formattedItems
    };
    setDestilacionRecords([...destilacionRecords, newRecord]);
    setIsFormOpen(false);
    alert(status === "revision" ? "¡Registro de 2da Destilación y cortes completado!" : "Borrador guardado.");
  };

  const handleSaveGranel = (status) => {
    const d = formDataGranel;
    const newRecord = {
      id: `GRA-0${granelRecords.length + 1}`,
      fechaMovimiento: d.fechaMovimiento,
      idContenedor: d.idContenedor,
      noLote: d.noLote,
      categoria: d.categoria,
      clase: d.clase,
      ingredientes: d.ingredientes,
      edad: d.edad,
      tipoMaguey: d.tipoMaguey,
      noAnalisisFisicoquimico: d.noAnalisis,
      invInicialVol: `${d.invInicialVol} L`,
      invInicialAlc: `${d.invInicialAlc}%`,
      entradaProcedencia: d.entradaProcedencia,
      entradaVolumen: `${d.entradaVolumen} L`,
      entradaAlc: `${d.entradaAlc}%`,
      entradaVolAgua: `${d.entradaVolAgua} L`,
      salidaVolumen: `${d.salidaVolumen} L`,
      salidaAlc: `${d.salidaAlc}%`,
      salidaDestino: d.salidaDestino,
      invFinalVol: `${d.invFinalVol} L`,
      invFinalAlc: `${d.invFinalAlc}%`,
      observaciones: d.observaciones || "Sin observaciones.",
      inspector: "Pendiente de V.Bo.",
      status: status
    };
    setGranelRecords([...granelRecords, newRecord]);
    setIsFormOpen(false);
    alert(status === "revision" ? "¡Movimiento de Inventario a Granel registrado exitosamente!" : "Borrador de inventario guardado.");
  };

  const handleSaveElaboracion = (status) => {
    const d = formDataElaboracion;
    const newRecord = {
      id: `ELAB-0${elaboracionRecords.length + 1}`,
      fechaIngreso: d.fechaIngreso,
      loteOrigen: d.loteOrigen,
      volumenOrigen: d.volumenOrigen,
      pctAlcOrigen: d.pctAlcOrigen,
      categoriaOrigen: d.categoriaOrigen,
      claseOrigen: d.claseOrigen,
      ingredientesOrigen: d.ingredientesOrigen,
      edadOrigen: d.edadOrigen,
      analisisOrigen: d.analisisOrigen,
      tipoMagueyOrigen: d.tipoMagueyOrigen,
      ingredientesAdicionados: d.ingredientesAdicionados,
      procesoRealizado: d.procesoRealizado,
      descripcionProceso: d.descripcionProceso,
      fechaSalida: d.fechaSalida,
      loteSalida: d.loteSalida,
      volumenSalida: d.volumenSalida,
      pctAlcSalida: d.pctAlcSalida,
      claseSalida: d.claseSalida,
      observaciones: d.observaciones || "Sin observaciones.",
      inspector: "Pendiente de V.Bo.",
      status: status
    };
    setElaboracionRecords([...elaboracionRecords, newRecord]);
    setIsFormOpen(false);
    alert(status === "revision" ? "¡Registro de Elaboración de Clase / Reproceso enviado!" : "Borrador guardado.");
  };

  const handleSaveMaduracion = async (status) => {
    const d = formDataMaduracion;
    if (!d.noLote) {
      alert("Por favor seleccione un Lote Origen de Granel.");
      return;
    }
    const recipientesList = (d.recipientes && d.recipientes.length > 0) ? d.recipientes : [d];

    const payload = {
      idLogin,
      fechaMovimiento: d.fechaMovimiento,
      noLote: d.noLote,
      granelOrigenId: d.granelOrigenId || null,
      categoria: d.categoria || 'Mezcal',
      clase: d.clase || 'Reposado',
      ingredientes: d.ingredientes || '',
      tipoMaguey: d.tipoMaguey || '',
      noAnalisis: d.noAnalisis || '',
      observaciones: d.observaciones || '',
      estatus: status,
      recipientes: recipientesList.map(rec => ({
        tipoRecipiente: rec.tipoRecipiente || 'BARRICAS DE MADERA DE',
        detalleRecipiente: rec.detalleRecipiente || '',
        noRecipientes: Number(rec.noRecipientes || rec.invInicialRecipientes || 1),
        invInicialFecha: rec.invInicialFecha || d.fechaMovimiento,
        invInicialRecipientes: Number(rec.invInicialRecipientes || 1),
        invInicialVolumen: Number(rec.invInicialVolumen || 0),
        invInicialAlc: Number(rec.invInicialAlc || 0),
        entradaFolio: rec.entradaFolio || '',
        entradaRecipientes: Number(rec.entradaRecipientes || 0),
        entradaVolumen: Number(rec.entradaVolumen || 0),
        entradaAlc: Number(rec.entradaAlc || 0),
        salidaRecipientes: Number(rec.salidaRecipientes || 0),
        salidaVolumen: Number(rec.salidaVolumen || 0),
        salidaAlc: Number(rec.salidaAlc || 0),
        claseObtenida: rec.salidaClaseObtenida || rec.claseObtenida || '',
        edadObtenida: rec.salidaEdadObtenida || rec.edadObtenida || '',
        salidaMerma: Number(rec.salidaMerma || 0),
        invFinalRecipientes: Number(rec.invFinalRecipientes || 1),
        invFinalVolumen: Number(rec.invFinalVolumen || 0),
        invFinalAlc: Number(rec.invFinalAlc || 0)
      }))
    };

    try {
      await dispatch(guardarMaduracionRecord(payload)).unwrap();
      setIsFormOpen(false);
      fetchMaduracionData();
      alert(status === "revision" ? "¡Movimiento de Maduración enviado para revisión oficial!" : "Borrador de maduración guardado exitosamente.");
    } catch (err) {
      alert(err?.mensaje || err?.message || (typeof err === "string" ? err : "Error al guardar el registro de maduración"));
    }
  };

  const handleSaveCondiciones = async (status) => {
    const d = formDataCondiciones;
    const mesStr = d.mes || "Julio";
    const anioStr = d.anio || String(new Date().getFullYear());

    const payload = {
      idLogin,
      mes: mesStr,
      anio: Number(anioStr),
      mesAnio: `${mesStr} ${anioStr}`,
      observaciones: d.observaciones || "",
      estatus: status,
      lecturas: (d.lecturas || []).map(lec => ({
        dia: Number(lec.dia || 1),
        hora: lec.hora || "12:00",
        temp: (lec.temp !== "" && lec.temp !== null && lec.temp !== undefined) ? Number(lec.temp) : null,
        hum: (lec.hum !== "" && lec.hum !== null && lec.hum !== undefined) ? Number(lec.hum) : null
      }))
    };

    try {
      await dispatch(guardarCondicionesRecord(payload)).unwrap();
      setIsFormOpen(false);
      fetchCondicionesData();
      alert(status === "revision" ? "¡Registro de Condiciones Ambientales enviado para revisión oficial!" : "Borrador de condiciones ambientales guardado exitosamente.");
    } catch (err) {
      alert(err?.mensaje || err?.message || (typeof err === "string" ? err : "Error al guardar el registro de condiciones ambientales"));
    }
  };

  const handleSaveQueja = (status) => {
    const d = formDataQuejas;
    const newRecord = {
      id: `QJ-PROD-0${quejasRecords.length + 1}`,
      fechaQueja: d.fechaQueja,
      noQueja: d.noQueja,
      noLote: d.noLote,
      descripcionProducto: d.descripcionProducto,
      detalleQueja: d.detalleQueja,
      fechaAtencion: d.fechaAtencion,
      medidasCorrectivas: d.medidasCorrectivas,
      inspector: "Pendiente de V.Bo.",
      status: status
    };
    setQuejasRecords([...quejasRecords, newRecord]);
    setIsFormOpen(false);
    alert(status === "revision" ? "¡Queja registrada exitosamente en Bitácora de Producción!" : "Borrador guardado.");
  };

  return (
    <div className="bg-glow-container min-h-screen p-4 md:p-8">
      {/* Decorative Blurs */}
      <div className="glow-blob-1"></div>
      <div className="glow-blob-2"></div>

      {/* Main SaaS Card */}
      <div className="bitacora-wrapper">

        {/* HEADER */}
        <header className="bitacora-header">
          <div className="header-logo-container">
            <div className="header-icon">
              <GrassIcon />
            </div>
            <div className="header-title-text">
              <span>Módulo de Trazabilidad Comercial</span>
              <h1>Bitácora de Producción de Mezcal</h1>
            </div>
          </div>

          <div className="header-controls">
            <span className={`status-badge ${tabsInfo[activeTab].theme}`}>
              {tabsInfo[activeTab].name}
            </span>
          </div>
        </header>

        {/* SECTIONS TABS AS CARDS */}
        <nav className="sections-grid">
          {tabsInfo.map((tab, index) => {
            const IconComponent = tab.icon;
            return (
              <button
                key={index}
                onClick={() => {
                  setActiveTab(index);
                  setIsFormOpen(false); // Reset to list view when tab changes
                }}
                className={`section-nav-card ${activeTab === index ? 'active' : ''}`}
              >
                <div className="card-icon-container">
                  <IconComponent style={{ fontSize: 22 }} />
                </div>
                <div>
                  <span className="card-title-span">{tab.title}</span>
                  <span className="card-name-span">{tab.name}</span>
                  <span className="card-desc-span">{tab.desc}</span>
                </div>
              </button>
            );
          })}
        </nav>

        {/* ACTIVE SECTION CONTENT */}
        <main className="content-section-panel">

          {/* ======================= PESTAÑA 1: COCCIÓN ======================= */}
          {activeTab === 0 && (
            <CoccionSection
              coccionRecords={displayCoccionRecords}
              pagination={coccionPagination}
              coccionSearch={coccionSearch}
              onSearchChange={handleCoccionSearchChange}
              onPageChange={handleCoccionPageChange}
              isFormOpen={isFormOpen}
              setIsFormOpen={setIsFormOpen}
              formData={formDataCoccion}
              setFormData={setFormDataCoccion}
              magueySpecies={magueySpecies}
              handleSaveCoccion={handleSaveCoccion}
              handleCancelCoccion={handleCancelCoccion}
              handleRejectCoccion={handleRejectCoccion}
              instalaciones={instalaciones}
            />
          )}

          {/* ======================= PESTAÑA 2: MOLIENDA & FERMENTACIÓN ======================= */}
          {activeTab === 1 && (
            <MoliendaSection
              moliendaRecords={displayMoliendaRecords}
              coccionRecords={displayCoccionRecords}
              isFormOpen={isFormOpen}
              setIsFormOpen={setIsFormOpen}
              formData={formDataMolienda}
              setFormData={setFormDataMolienda}
              handleSaveMolienda={handleSaveMolienda}
              handleCancelMolienda={handleCancelMolienda}
              handleRejectMolienda={handleRejectMolienda}
            />
          )}

          {/* ======================= PESTAÑA 3: DESTILACIÓN ======================= */}
          {activeTab === 2 && (
            <DestilacionSection
              destilacionRecords={destilacionRecords}
              moliendaRecords={moliendaRecords}
              isFormOpen={isFormOpen}
              setIsFormOpen={setIsFormOpen}
              formData={formDataDestilacion}
              setFormData={setFormDataDestilacion}
              handleSaveDestilacion={handleSaveDestilacion}
              handleCancelDestilacion={handleCancelDestilacion}
              handleRejectDestilacion={handleRejectDestilacion}
            />
          )}

          {/* ======================= PESTAÑA 4: GRANEL Y CIERRE ======================= */}
          {activeTab === 3 && (
            <GranelSection
              granelRecords={granelRecords}
              destilacionRecords={destilacionRecords}
              isFormOpen={isFormOpen}
              setIsFormOpen={setIsFormOpen}
              formData={formDataGranel}
              setFormData={setFormDataGranel}
              handleSaveGranel={handleSaveGranel}
              handleCancelGranel={handleCancelGranel}
              handleRejectGranel={handleRejectGranel}
            />
          )}

          {/* ======================= PESTAÑA 5: SECCIÓN VI - ELABORACIÓN CLASES / REPROCESOS ======================= */}
          {activeTab === 4 && (
            <ElaboracionSection
              elaboracionRecords={elaboracionRecords}
              granelRecords={granelRecords}
              isFormOpen={isFormOpen}
              setIsFormOpen={setIsFormOpen}
              formData={formDataElaboracion}
              setFormData={setFormDataElaboracion}
              handleSaveElaboracion={handleSaveElaboracion}
            />
          )}

          {/* ======================= PESTAÑA 6: SECCIÓN VII - PROCESO DE MADURACIÓN ======================= */}
          {activeTab === 5 && (
            <MaduracionSection
              maduracionRecords={maduracionApiRecords.length > 0 ? maduracionApiRecords : maduracionRecords}
              destilacionRecords={destilacionRecords}
              granelRecords={granelRecords}
              isFormOpen={isFormOpen}
              setIsFormOpen={setIsFormOpen}
              formData={formDataMaduracion}
              setFormData={setFormDataMaduracion}
              handleSaveMaduracion={handleSaveMaduracion}
              handleCancelMaduracion={(record) => handleOpenCancelModal(record, "maduracion")}
              handleRejectMaduracion={(record) => handleOpenRejectModal(record, "maduracion")}
            />
          )}

          {/* ======================= PESTAÑA 7: SECCIÓN VIII - CONDICIONES DE MADURACIÓN ======================= */}
          {activeTab === 6 && (
            <CondicionesSection
              condicionesRecords={condicionesApiRecords.length > 0 ? condicionesApiRecords : condicionesRecords}
              destilacionRecords={destilacionRecords}
              isFormOpen={isFormOpen}
              setIsFormOpen={setIsFormOpen}
              formData={formDataCondiciones}
              setFormData={setFormDataCondiciones}
              handleSaveCondiciones={handleSaveCondiciones}
              handleCancelCondiciones={(record) => handleOpenCancelModal(record, "condiciones")}
              handleRejectCondiciones={(record) => handleOpenRejectModal(record, "condiciones")}
            />
          )}

          {/* ======================= PESTAÑA 8: SECCIÓN IX - QUEJAS ACERCA DEL PRODUCTO CERTIFICADO ======================= */}
          {activeTab === 7 && (
            <QuejasSection
              quejasRecords={quejasRecords}
              granelRecords={granelRecords}
              isFormOpen={isFormOpen}
              setIsFormOpen={setIsFormOpen}
              formData={formDataQuejas}
              setFormData={setFormDataQuejas}
              handleSaveQueja={handleSaveQueja}
            />
          )}

        </main>
      </div>

      {/* MODAL UNIFICADO PARA MOTIVOS DE RECHAZO Y CANCELACIÓN */}
      {statusModal.isOpen && createPortal(
        <div style={{
          position: "fixed",
          top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(0, 0, 0, 0.75)",
          backdropFilter: "blur(4px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 99999,
          padding: "20px"
        }}>
          <div style={{
            background: "#161b22",
            border: statusModal.actionType === "rechazado" ? "1px solid rgba(245, 158, 11, 0.4)" : "1px solid rgba(239, 68, 68, 0.4)",
            borderRadius: "16px",
            maxWidth: "500px",
            width: "100%",
            padding: "24px",
            boxShadow: "0 20px 40px rgba(0,0,0,0.5)",
            color: "white"
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
              {statusModal.actionType === "rechazado" ? (
                <div style={{ background: "rgba(245, 158, 11, 0.15)", color: "#F59E0B", padding: "10px", borderRadius: "12px" }}>
                  <ReportProblemIcon style={{ fontSize: 28 }} />
                </div>
              ) : (
                <div style={{ background: "rgba(239, 68, 68, 0.15)", color: "#EF4444", padding: "10px", borderRadius: "12px" }}>
                  <CancelIcon style={{ fontSize: 28 }} />
                </div>
              )}
              <div>
                <h3 style={{ margin: 0, fontSize: "18px", fontWeight: 800 }}>
                  {statusModal.actionType === "rechazado" ? "Rechazar Bitácora (Para Corrección)" : "Cancelar Bitácora"}
                </h3>
                <span style={{ fontSize: "12.5px", color: "var(--text-muted)" }}>
                  Registro: {statusModal.record?.noProduccion || statusModal.record?.noLote || statusModal.record?.idContenedor || 'Seleccionado'}
                </span>
              </div>
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label style={{ display: "block", fontSize: "13px", fontWeight: 700, marginBottom: "8px", color: "#E2E8F0" }}>
                {statusModal.actionType === "rechazado"
                  ? "Observaciones de Corrección (Obligatorias para el cliente):"
                  : "Motivo de Cancelación (Obligatorio):"}
              </label>
              <textarea
                rows={4}
                value={statusModal.observaciones}
                onChange={(e) => setStatusModal(prev => ({ ...prev, observaciones: e.target.value }))}
                placeholder={statusModal.actionType === "rechazado"
                  ? "Ej: Indique los datos a corregir en peso de maguey o fechas de fermentación..."
                  : "Ej: Se cancela la bitácora debido a que..."
                }
                style={{
                  width: "100%",
                  background: "#0d1117",
                  border: "1px solid #30363d",
                  borderRadius: "10px",
                  padding: "12px",
                  color: "white",
                  fontSize: "13.5px",
                  outline: "none",
                  resize: "vertical"
                }}
              />
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
              <button
                onClick={() => setStatusModal({ isOpen: false, record: null, actionType: "", section: "", observaciones: "" })}
                style={{
                  background: "transparent",
                  border: "1px solid #475569",
                  color: "#94A3B8",
                  borderRadius: "10px",
                  padding: "10px 18px",
                  fontWeight: 700,
                  cursor: "pointer"
                }}
              >
                Cancelar
              </button>

              <button
                onClick={handleConfirmStatusAction}
                style={{
                  background: statusModal.actionType === "rechazado" ? "#D97706" : "#DC2626",
                  color: "white",
                  border: "none",
                  borderRadius: "10px",
                  padding: "10px 20px",
                  fontWeight: 800,
                  cursor: "pointer",
                  boxShadow: statusModal.actionType === "rechazado" ? "0 4px 12px rgba(217, 119, 6, 0.3)" : "0 4px 12px rgba(220, 38, 38, 0.3)"
                }}
              >
                {statusModal.actionType === "rechazado" ? "Confirmar Rechazo" : "Confirmar Cancelación"}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

    </div>
  );
};

export default BitacoraProduccion;